// Content loader for markdown-driven marketing pages.
//
// Markdown lives in apps/web/content/<folder>/<slug>.md. The vite-plugins/markdown
// plugin transforms each .md file at build time into a JS module exporting
// { frontmatter, html, slug }. We collect them via import.meta.glob and expose
// helpers for fetching content by folder/slug, with publishedAt date gating.

export type ContentFolder = 'blog' | 'compare' | 'use-cases' | 'features' | 'help' | 'pricing'

export interface ContentItem {
  /** Folder name in /content/ */
  folder: ContentFolder
  /** Slug from frontmatter or filename */
  slug: string
  /** All frontmatter fields */
  frontmatter: Record<string, unknown>
  /** Pre-rendered HTML body */
  html: string
  /** Original frontmatter title (if present) */
  title?: string
  /** Original frontmatter description (if present) */
  description?: string
  /** publishedAt as a JS Date, or null if unparseable / missing */
  publishedAt: Date | null
}

interface ContentModule {
  frontmatter: Record<string, unknown>
  html: string
  slug: string
}

// Import every .md under content/. import.meta.glob is statically analyzable
// so Vite includes only what exists at build time.
const modules = import.meta.glob<ContentModule>('../../content/**/*.md', { eager: true })

const items: ContentItem[] = Object.entries(modules).flatMap(([path, mod]) => {
  // path looks like "../../content/blog/post-slug.md"
  const match = path.match(/\/content\/([^/]+)\/([^/]+)\.md$/)
  if (!match) return []
  const folder = match[1] as ContentFolder
  const slug = (mod.frontmatter.slug as string | undefined) || match[2]!
  const publishedAtRaw = mod.frontmatter.publishedAt as string | undefined
  const publishedAt = publishedAtRaw ? new Date(publishedAtRaw) : null
  const title = mod.frontmatter.title as string | undefined
  const description = mod.frontmatter.description as string | undefined
  const item: ContentItem = {
    folder,
    slug,
    frontmatter: mod.frontmatter,
    html: mod.html,
    publishedAt,
    ...(title !== undefined ? { title } : {}),
    ...(description !== undefined ? { description } : {}),
  }
  return [item]
})

const TODAY = new Date()

function isPublished(item: ContentItem): boolean {
  // Items without publishedAt are treated as always-published (e.g., evergreen
  // pricing or use-case pages). Only future-dated items are gated.
  if (!item.publishedAt) return true
  return item.publishedAt.getTime() <= TODAY.getTime()
}

export function getAllContent(folder: ContentFolder, opts: { includeUnpublished?: boolean } = {}): ContentItem[] {
  const filtered = items.filter((i) => i.folder === folder && (opts.includeUnpublished || isPublished(i)))
  return filtered.sort((a, b) => {
    const aT = a.publishedAt?.getTime() ?? 0
    const bT = b.publishedAt?.getTime() ?? 0
    return bT - aT
  })
}

export function getContentBySlug(folder: ContentFolder, slug: string): ContentItem | undefined {
  const item = items.find((i) => i.folder === folder && i.slug === slug)
  if (!item) return undefined
  if (!isPublished(item)) return undefined
  return item
}

/** All published content as a flat list — useful for sitemap generation. */
export function getAllPublished(): ContentItem[] {
  return items.filter(isPublished)
}
